import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Dumbbell, Clock, Check, AlertCircle, Plus, Edit3, Calendar, GripVertical, ArrowRight, Home } from 'lucide-react';
import FullScreenWorkoutEditor from '../workout/FullScreenWorkoutEditor';
import TemplateSelectorPanel from '../workout/TemplateSelectorPanel';
import { ContinuityChip, useContinuity } from '../trainer/ContinuityChip';
import { DeloadToggleButton } from '../cycles/DeloadToggleButton';
import DayReserveModal from './DayReserveModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format date to YYYY-MM-DD using LOCAL timezone (not UTC)
// CRITICAL: toISOString() returns UTC which causes date shift at midnight
const formatLocalDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get Monday of the week for a given date
const getMondayOfWeek = (dateStr) => {
  const date = new Date(dateStr + 'T12:00:00');
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(date.setDate(diff));
  return formatLocalDate(monday);
};

const BookingPlanner = ({ clientId, clientName, t }) => {
  const [searchParams] = useSearchParams();
  // Operational continuity signal (trainer-only). Refreshed when the
  // planner reloads after edits/saves.
  const { data: continuity, refresh: refreshContinuity } = useContinuity(clientId);
  const [reserveDate, setReserveDate] = useState(null);
  const initialDate = searchParams.get('date');
  
  const [weekStart, setWeekStart] = useState(() => {
    // If date param provided, navigate to that week
    if (initialDate) {
      return getMondayOfWeek(initialDate);
    }
    // Default to current week
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1);
    return formatLocalDate(monday);
  });
  
  // Track the highlighted date from URL
  const [highlightedDate, setHighlightedDate] = useState(initialDate || null);
  
  const [plannerData, setPlannerData] = useState(null);
  const [recentWeeks, setRecentWeeks] = useState([]); // [{ weekStart, weekEnd, data }, ...] oldest→newest
  const [recentWeeksDepth, setRecentWeeksDepth] = useState(() => {
    // Persisted per-trainer depth preference (4 / 6 / 8)
    try {
      const stored = localStorage.getItem('plannerRecentWeeksDepth');
      const n = parseInt(stored, 10);
      return [4, 6, 8].includes(n) ? n : 4;
    } catch {
      return 4;
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [assigningTemplate, setAssigningTemplate] = useState(false);
  // For direct workout editing
  const [editingWorkout, setEditingWorkout] = useState(null);

  // Drag & Drop state
  const [draggedItem, setDraggedItem] = useState(null); // { workoutId, bookingId, date, time, exerciseCount, status, isFinished }
  const [dropTarget, setDropTarget] = useState(null); // dayKey being hovered
  const [dropConfirm, setDropConfirm] = useState(null); // { dayKey, date, time, workoutId } — inline confirm for new booking
  const [isMoving, setIsMoving] = useState(false);
  const dropConfirmTimeRef = useRef(null);

  // Fetch planner data (current + N recent weeks)
  const fetchPlannerData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Build list of week_start values: N recent weeks (oldest→newest) + current
      const recentStarts = [];
      for (let i = recentWeeksDepth; i >= 1; i--) {
        const d = new Date(weekStart + 'T12:00:00');
        d.setDate(d.getDate() - 7 * i);
        recentStarts.push(formatLocalDate(d));
      }

      // Fetch in parallel
      const requests = [
        axios.get(`${API}/bookings/client/${clientId}/weekly-planner?week_start=${weekStart}`, { headers }),
        ...recentStarts.map((ws) =>
          axios
            .get(`${API}/bookings/client/${clientId}/weekly-planner?week_start=${ws}`, { headers })
            .catch(() => ({ data: null }))
        ),
      ];
      const responses = await Promise.all(requests);

      setPlannerData(responses[0].data);
      const recent = responses.slice(1).map((res, idx) => ({
        weekStart: recentStarts[idx],
        data: res.data,
      }));
      setRecentWeeks(recent);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load planner');
    } finally {
      setLoading(false);
    }
  }, [clientId, weekStart, recentWeeksDepth]);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/workouts?client_id=template&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to only templates from workout_templates collection if available
      // For now, templates might still come from old endpoint
      setTemplates(response.data || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  }, []);

  // Fetch exercises for the workout editor
  const fetchExercises = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/exercises/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExercises(response.data || []);
    } catch (err) {
      console.error('Failed to fetch exercises:', err);
    }
  }, []);

  useEffect(() => {
    fetchPlannerData();
    fetchTemplates();
    fetchExercises();
  }, [fetchPlannerData, fetchTemplates, fetchExercises]);

  // Refresh continuity when planner data reloads (workouts/deload may have changed)
  useEffect(() => {
    if (plannerData) refreshContinuity();
  }, [plannerData, refreshContinuity]);

  // Navigate weeks
  const navigateWeek = (direction) => {
    const current = new Date(weekStart);
    current.setDate(current.getDate() + (direction * 7));
    setWeekStart(formatLocalDate(current));
  };

  // Assign template to booking
  const handleAssignTemplate = async (templateId) => {
    if (!selectedBooking || assigningTemplate) return;
    
    try {
      setAssigningTemplate(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/bookings/${selectedBooking.booking.id}/create-workout-from-template?template_id=${templateId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowTemplateModal(false);
      setSelectedBooking(null);
      fetchPlannerData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to assign template');
    } finally {
      setAssigningTemplate(false);
    }
  };

  // Handle edit workout - open the editor directly without URL params
  const handleEditWorkout = (booking, workout) => {
    if (!workout || !workout.id) {
      console.error('Cannot edit workout: workout or workout.id is missing');
      return;
    }
    
    // Determine mode based on booking/workout status
    const getEditorMode = () => {
      if (!booking) return 'plan';
      const bookingDate = new Date(booking.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      bookingDate.setHours(0, 0, 0, 0);
      
      if (workout?.status === 'finished') {
        return 'results';
      }
      if (bookingDate < today) {
        return 'entry';
      }
      return 'plan';
    };
    
    // Create a booking-like object for the editor
    const bookingForEditor = {
      id: booking.id,
      client_name: clientName || booking.client_name,
      date: booking.date,
      start_time: booking.start_time,
      end_time: booking.end_time,
      status: booking.status
    };
    
    setEditingWorkout({
      booking: bookingForEditor,
      workout: workout,
      mode: getEditorMode()
    });
  };

  // Handle save workout from editor
  // Supports both creating new workouts and updating existing ones
  const handleSaveWorkout = async (booking, existingWorkout, exercises, times, mode) => {
    const safeInt = (val, fallback) => {
      const num = parseInt(val);
      return isNaN(num) ? fallback : num;
    };
    const safeFloat = (val, fallback) => {
      const num = parseFloat(val);
      return isNaN(num) ? fallback : num;
    };
    
    try {
      const token = localStorage.getItem('token');
      
      // Format exercises with all required fields
      const exerciseData = exercises.map(ex => ({
        exercise_id: ex.exercise_id || '',
        exercise_name: ex.exercise_name || 'Unknown Exercise',
        // Required fields for backend validation
        sets: safeInt(ex.target_sets || ex.sets, 4),
        reps: safeInt(ex.target_reps || ex.reps, 12),
        weight: safeFloat(ex.target_weight || ex.weight, 0),
        // Target fields for UI display
        target_sets: safeInt(ex.target_sets || ex.sets, 4),
        target_reps: safeInt(ex.target_reps || ex.reps, 12),
        target_weight: safeFloat(ex.target_weight || ex.weight, 0),
        rest_time: safeInt(ex.rest_time, 120),
        is_superset: ex.is_superset || false,
        superset_group: ex.is_superset ? (ex.superset_group || 'A') : (ex.superset_group || null),
        actual_sets: ex.actual_sets || [],
        note: ex.note || '',
        notes: ex.notes || '',
        image_url: ex.image_url || '',
        animation_webp: ex.animation_webp || '',
        instructions: ex.instructions || '',
        instructions_es: ex.instructions_es || '',
        muscle_groups: ex.muscle_groups || [],
        difficulty: ex.difficulty || '',
        equipment: ex.equipment || ''
      }));
      
      let workoutId;
      
      if (existingWorkout && existingWorkout.id) {
        // Update existing workout
        workoutId = existingWorkout.id;
      } else {
        // Create new workout first
        const response = await axios.post(
          `${API}/bookings/${booking.id}/create-workout`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        workoutId = response.data.id;
      }
      
      // Save exercises
      await axios.put(
        `${API}/workouts/${workoutId}/set-exercises`,
        exerciseData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update workout times if provided
      if (times.total_time > 0 || times.rest_time > 0) {
        const activeTime = times.total_time - times.rest_time;
        await axios.put(
          `${API}/workouts/${workoutId}`,
          {
            total_time: times.total_time,
            rest_time: times.rest_time,
            duration: activeTime > 0 ? activeTime : 0
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      // Refresh data after save
      fetchPlannerData();
    } catch (err) {
      console.error('Error saving workout:', err);
      throw err;
    }
  };

  // Handle create workout - opens editor in plan mode with no existing workout
  const handleCreateWorkout = (booking) => {
    // Create a booking-like object for the editor
    const bookingForEditor = {
      id: booking.id,
      client_name: clientName || booking.client_name,
      date: booking.date,
      start_time: booking.start_time,
      end_time: booking.end_time,
      status: booking.status
    };
    
    // Open editor in plan mode with null workout (will create on save)
    setEditingWorkout({
      booking: bookingForEditor,
      workout: null,
      mode: 'plan'
    });
  };

  // Handle finish workout
  const handleFinishWorkout = async (workoutId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/workouts/${workoutId}/finish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingWorkout(null);
      fetchPlannerData();
    } catch (err) {
      console.error('Error finishing workout:', err);
      alert(err.response?.data?.detail || 'Failed to finish workout');
    }
  };

  // Format time display
  const formatTime = (time) => {
    if (!time) return '';
    return time.substring(0, 5);
  };

  // =============================================
  // DRAG & DROP HANDLERS
  // =============================================
  
  const handleDragStart = (e, item) => {
    // Block drag for in_progress workouts
    if (item.workout?.status === 'in_progress') {
      e.preventDefault();
      return;
    }
    
    const workoutData = {
      workoutId: item.workout.id,
      bookingId: item.booking.id,
      date: item.booking.date,
      time: item.booking.start_time,
      exerciseCount: item.workout.exercises?.length || 0,
      status: item.workout.status,
      isFinished: item.is_finished
    };
    
    setDraggedItem(workoutData);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify(workoutData));
    
    // Style the drag ghost
    if (e.target) {
      e.target.style.opacity = '0.5';
    }
  };
  
  const handleDragEnd = (e) => {
    if (e.target) {
      e.target.style.opacity = '1';
    }
    setDraggedItem(null);
    setDropTarget(null);
  };
  
  const handleDragOver = (e, dayKey) => {
    e.preventDefault();
    // Match the drop effect to the dragged item's effectAllowed.
    // Mismatched effects cause the browser to silently reject the drop.
    e.dataTransfer.dropEffect = (draggedItem?.source === 'prev') ? 'copy' : 'move';
    setDropTarget(dayKey);
  };
  
  const handleDragLeave = (e, dayKey) => {
    // Only clear if actually leaving the day column (not entering a child)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      if (dropTarget === dayKey) setDropTarget(null);
    }
  };
  
  const handleDrop = async (e, dayKey, dayData) => {
    e.preventDefault();
    setDropTarget(null);
    
    if (!draggedItem) return;
    
    const targetDate = dayData?.date;
    if (!targetDate) return;
    
    // Branch: cloning from a previous-week workout (read-only source)
    if (draggedItem.source === 'prev') {
      const targetBookings = dayData?.bookings || [];
      const emptyBooking = targetBookings.find((b) => !b.has_workout);
      const allOccupied = targetBookings.length > 0 && !emptyBooking;
      
      if (allOccupied) {
        alert('Este día ya tiene rutina asignada. Bórrala primero o usa otro día.');
        setDraggedItem(null);
        return;
      }
      
      const ok = window.confirm(
        emptyBooking
          ? `${draggedItem.isFinished ? '↻ Repetir' : 'Reusar'} "${draggedItem.templateName}" (${draggedItem.exerciseCount} ejercicios) en ${targetDate}?\n\n${draggedItem.isFinished ? 'Se replicará la estructura exacta por serie (reps, peso, descanso).' : 'Se creará una nueva rutina enlazada a la sesión existente.'}`
          : `${draggedItem.isFinished ? '↻ Repetir' : 'Reusar'} "${draggedItem.templateName}" como Rutina libre en ${targetDate}?\n\n${draggedItem.isFinished ? 'Se replicará la estructura exacta por serie.' : 'Se creará una nueva rutina sin cita.'}`
      );
      if (!ok) {
        setDraggedItem(null);
        return;
      }
      
      // Branch: completed source → /replay (preserves per-set structure).
      // Non-completed source → existing clone (carries planned values only).
      if (draggedItem.isFinished) {
        await executeReplayWorkout(
          draggedItem.workoutId,
          emptyBooking ? emptyBooking.booking.id : null,
          targetDate
        );
      } else {
        await executeCloneWorkout(
          draggedItem,
          emptyBooking ? emptyBooking.booking.id : null,
          targetDate
        );
      }
      setDraggedItem(null);
      return;
    }
    
    // Same day? No-op
    if (targetDate === draggedItem.date) {
      setDraggedItem(null);
      return;
    }
    
    // Find bookings on target day
    const targetBookings = dayData?.bookings || [];
    
    // Look for a booking WITHOUT a workout on the target day
    const emptyBooking = targetBookings.find(b => !b.has_workout);
    
    // Look for any booking WITH a workout (conflict)
    const occupiedBooking = targetBookings.find(b => b.has_workout && b.workout?.id !== draggedItem.workoutId);
    
    if (emptyBooking) {
      // Case 1: Drop on day WITH available booking → move directly
      await executeMoveWorkout(draggedItem.workoutId, emptyBooking.booking.id, null, null, draggedItem.isFinished);
    } else if (targetBookings.length > 0 && !emptyBooking) {
      // All bookings on this day already have workouts → show inline confirm for new booking
      setDropConfirm({
        dayKey,
        date: targetDate,
        time: draggedItem.time || '10:00',
        workoutId: draggedItem.workoutId,
        isFinished: draggedItem.isFinished
      });
    } else {
      // Case 2: No bookings at all → show inline confirm to create booking
      setDropConfirm({
        dayKey,
        date: targetDate,
        time: draggedItem.time || '10:00',
        workoutId: draggedItem.workoutId,
        isFinished: draggedItem.isFinished
      });
    }
    
    setDraggedItem(null);
  };
  
  const executeReplayWorkout = async (sourceWorkoutId, targetBookingId, targetDate) => {
    // Calls the backend /replay endpoint, which preserves the EXACT per-set
    // structure (reps/weight/rest_time) of what was actually performed.
    // RPE is intentionally NOT carried.
    try {
      setIsMoving(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/workouts/${sourceWorkoutId}/replay`,
        { date: targetDate, booking_id: targetBookingId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPlannerData();
    } catch (err) {
      const detail = err.response?.data?.detail || 'No se pudo repetir la sesión';
      alert(detail);
    } finally {
      setIsMoving(false);
    }
  };
  
  const executeCloneWorkout = async (source, targetBookingId, targetDate) => {
    // Strip executed data + per-exercise dynamic fields → clean re-usable plan
    const cleanExercises = (source.exercises || []).map((ex) => ({
      exercise_id: ex.exercise_id,
      exercise_name: ex.exercise_name,
      exercise_name_es: ex.exercise_name_es,
      sets: ex.sets || 3,
      reps: ex.reps || 10,
      weight: ex.weight || 0,
      rest_time: ex.rest_time || 60,
      notes: ex.notes || '',
      target_weight: ex.target_weight,
      target_reps: ex.target_reps,
      target_sets: ex.target_sets,
      image_url: ex.image_url,
      animation_webp: ex.animation_webp,
      muscle_groups: ex.muscle_groups,
      difficulty: ex.difficulty,
      equipment: ex.equipment,
      is_superset: ex.is_superset || false,
      superset_group: ex.is_superset ? (ex.superset_group || 'A') : (ex.superset_group || null),
      role: ex.role,
      slot_label: ex.slot_label,
      actual_sets: [],
    }));
    
    const payload = {
      client_id: clientId,
      booking_id: targetBookingId || null,
      date: targetDate,
      mode: targetBookingId ? 'session' : 'flexible',
      template_name: source.templateName,
      exercises: cleanExercises,
    };
    
    try {
      setIsMoving(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/workouts`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchPlannerData();
    } catch (err) {
      const detail = err.response?.data?.detail || 'No se pudo clonar la rutina';
      alert(detail);
    } finally {
      setIsMoving(false);
    }
  };
  
  const executeMoveWorkout = async (workoutId, targetBookingId, targetDate, targetTime, isFinished) => {
    // Completed workout: require confirmation
    if (isFinished) {
      const confirmed = window.confirm('This workout is completed. Moving it will change its linked booking.\n\nAll exercises and data will be preserved.\n\nContinue?');
      if (!confirmed) return;
    }
    
    try {
      setIsMoving(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ workout_id: workoutId });
      if (targetBookingId) params.set('target_booking_id', targetBookingId);
      if (targetDate) params.set('target_date', targetDate);
      if (targetTime) params.set('target_time', targetTime);
      if (isFinished) params.set('force', 'true');
      
      await axios.post(
        `${API}/bookings/planner/move-workout?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setDropConfirm(null);
      fetchPlannerData();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to move workout';
      alert(detail);
    } finally {
      setIsMoving(false);
    }
  };
  
  const handleConfirmDrop = () => {
    if (!dropConfirm) return;
    const time = dropConfirmTimeRef.current?.value || dropConfirm.time;
    executeMoveWorkout(dropConfirm.workoutId, null, dropConfirm.date, time, dropConfirm.isFinished);
  };
  
  const handleCancelDrop = () => {
    setDropConfirm(null);
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const colors = {
      reserved: 'bg-yellow-500/20 text-yellow-300',
      confirmed: 'bg-blue-500/20 text-blue-300',
      assigned: 'bg-purple-500/20 text-purple-300',
      completed: 'bg-green-500/20 text-green-300',
      no_show: 'bg-red-500/20 text-red-300'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-300';
  };

  // Format week display
  const formatWeekDisplay = () => {
    if (!plannerData) return '';
    const start = new Date(plannerData.week_start);
    const end = new Date(plannerData.week_end);
    const options = { month: 'short', day: 'numeric' };
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}, ${start.getFullYear()}`;
  };

  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  // Derive workout focus label from exercise muscle groups
  const getWorkoutFocus = (workout) => {
    if (!workout?.exercises || workout.exercises.length === 0) return null;
    const muscles = new Set();
    for (const ex of workout.exercises) {
      for (const mg of (ex.muscle_groups || [])) {
        muscles.add(mg.toLowerCase());
      }
      // Also check exercise_name patterns as fallback
      const name = (ex.exercise_name || '').toLowerCase();
      if (name.includes('squat') || name.includes('leg') || name.includes('lunge') || name.includes('calf') || name.includes('deadlift') || name.includes('hip')) muscles.add('legs');
      if (name.includes('bench') || name.includes('press') || name.includes('fly') || name.includes('push') || name.includes('tricep') || name.includes('shoulder') || name.includes('delt')) muscles.add('upper');
      if (name.includes('row') || name.includes('pull') || name.includes('lat') || name.includes('bicep') || name.includes('back') || name.includes('curl')) muscles.add('upper');
      if (name.includes('plank') || name.includes('crunch') || name.includes('ab') || name.includes('core')) muscles.add('core');
    }
    const hasUpper = muscles.has('chest') || muscles.has('back') || muscles.has('shoulders') || muscles.has('arms') || muscles.has('upper');
    const hasLower = muscles.has('legs') || muscles.has('glutes') || muscles.has('quadriceps') || muscles.has('hamstrings') || muscles.has('calves');
    const hasCore = muscles.has('core') || muscles.has('abs');
    if (hasUpper && hasLower) return { label: 'Full Body', color: 'text-purple-300 bg-purple-500/20' };
    if (hasUpper) return { label: 'Upper', color: 'text-blue-300 bg-blue-500/20' };
    if (hasLower) return { label: 'Lower', color: 'text-green-300 bg-green-500/20' };
    if (hasCore) return { label: 'Core', color: 'text-orange-300 bg-orange-500/20' };
    return { label: 'Workout', color: 'text-gray-300 bg-gray-500/20' };
  };

  // Persist depth changes
  const handleDepthChange = (n) => {
    setRecentWeeksDepth(n);
    try {
      localStorage.setItem('plannerRecentWeeksDepth', String(n));
    } catch { /* ignore quota / private-mode errors */ }
  };

  // Format previous week display (kept for any consumer; unused after refactor)
  const formatPrevWeekDisplay = () => {
    if (!recentWeeks?.length) return '';
    const last = recentWeeks[recentWeeks.length - 1];
    if (!last?.data) return '';
    const start = new Date(last.data.week_start + 'T12:00:00');
    const end = new Date(last.data.week_end + 'T12:00:00');
    const options = { month: 'short', day: 'numeric' };
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="planner-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-red-400" data-testid="planner-error">
        <AlertCircle className="w-5 h-5 mr-2" />
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="booking-planner">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-wrap">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-400" />
            Weekly Planner
          </h2>
          <span className="text-gray-400 text-sm">
            {plannerData?.total_bookings || 0} bookings · {plannerData?.total_workouts || 0} workouts
          </span>
          {/* Operational continuity signal (trainer-only) */}
          {continuity && (
            <ContinuityChip data={continuity} variant="chip" />
          )}
        </div>
        
        {/* Week Navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigateWeek(-1)}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            data-testid="prev-week-btn"
          >
            <ChevronLeft className="w-5 h-5 text-gray-400" />
          </button>
          <span className="text-white font-medium min-w-[200px] text-center">
            {formatWeekDisplay()}
          </span>
          <button
            onClick={() => navigateWeek(1)}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            data-testid="next-week-btn"
          >
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>
        </div>
      </div>

      {/* ===== RECENT WEEKS — Drag source for replay / clone =====
          Shows the last N weeks with FULL exercise overview so the trainer
          can scan history and drag any past session onto the current week.
          A completed session triggers /replay (preserves per-set structure);
          a scheduled session triggers the existing clone (plan only). */}
      {recentWeeks.length > 0 && (
        <div data-testid="recent-weeks-strip">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {recentWeeksDepth} últimas semanas
            </span>
            <div className="flex items-center gap-1 text-[10px]" data-testid="recent-weeks-depth-selector">
              <span className="text-gray-500 mr-1">Ver</span>
              {[4, 6, 8].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => handleDepthChange(n)}
                  className={`px-2 py-0.5 rounded transition ${
                    recentWeeksDepth === n
                      ? 'bg-blue-500/30 text-blue-100 font-semibold'
                      : 'text-gray-500 hover:text-gray-200 hover:bg-white/[0.04]'
                  }`}
                  data-testid={`depth-${n}-btn`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            {recentWeeks.map(({ weekStart: ws, data }) => {
              if (!data) return null;
              const hasAnything = (data.total_bookings || 0) > 0 || (data.total_workouts || 0) > 0;
              const rangeStart = new Date(data.week_start + 'T12:00:00');
              const rangeEnd = new Date(data.week_end + 'T12:00:00');
              const opts = { month: 'short', day: 'numeric' };
              const rangeLabel = `${rangeStart.toLocaleDateString('en-US', opts)} – ${rangeEnd.toLocaleDateString('en-US', opts)}`;

              return (
                <div
                  key={`recent-${ws}`}
                  className="opacity-70 hover:opacity-100 transition-opacity"
                  data-testid={`recent-week-${ws}`}
                >
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5 pl-1">
                    {rangeLabel}
                  </div>
                  <div className="grid grid-cols-6 gap-1">
                    {dayKeys.map((dayKey, index) => {
                      const dayData = data?.days?.[dayKey];
                      const bookings = dayData?.bookings || [];
                      return (
                        <div
                          key={`recent-${ws}-${dayKey}`}
                          className="rounded-md bg-gray-800/25 p-1 min-h-[60px]"
                          data-testid={`recent-day-${ws}-${dayKey}`}
                        >
                          <div className="text-center mb-0.5">
                            <span className="text-[9px] text-gray-500 font-medium">{dayLabels[index]}</span>
                            <span className="text-[10px] text-gray-500 ml-1">
                              {dayData?.date ? new Date(dayData.date + 'T12:00:00').getDate() : ''}
                            </span>
                          </div>
                          {bookings.length > 0 ? bookings.map((item) => {
                            const focus = item.has_workout ? getWorkoutFocus(item.workout) : null;
                            const isCloneable = item.has_workout && item.workout
                              && (item.workout.exercises || []).length > 0
                              && item.workout.status !== 'in_progress';
                            const dragGlyph = item.is_finished ? '↻' : '↗';
                            const dragTitle = item.is_finished
                              ? 'Arrastra para repetir esta sesión (estructura por serie exacta)'
                              : 'Arrastra para reusar el plan';
                            return (
                              <div key={item.booking.id} className="mb-0.5 last:mb-0">
                                {item.has_workout && item.workout ? (
                                  <div
                                    className={`relative group rounded px-1 py-0.5 ${item.is_finished ? 'bg-green-900/20 border border-green-800/30' : 'bg-purple-900/20 border border-purple-800/30'} ${isCloneable ? 'cursor-grab active:cursor-grabbing hover:ring-1 hover:ring-cyan-400/50 select-none' : ''}`}
                                    draggable={isCloneable}
                                    onMouseDown={() => {
                                      if (isCloneable) {
                                        try { window.getSelection()?.removeAllRanges(); } catch { /* no-op */ }
                                      }
                                    }}
                                    onDragStart={(e) => {
                                      if (!isCloneable) return;
                                      const data2 = {
                                        source: 'prev',
                                        workoutId: item.workout.id,
                                        templateName: item.workout.template_name || focus?.label || 'Rutina',
                                        exercises: item.workout.exercises,
                                        exerciseCount: item.workout.exercises.length,
                                        isFinished: !!item.is_finished,
                                      };
                                      setDraggedItem(data2);
                                      e.dataTransfer.effectAllowed = 'copy';
                                      e.dataTransfer.setData('text/plain', JSON.stringify({ ...data2, exercises: undefined }));
                                      if (e.currentTarget) e.currentTarget.style.opacity = '0.5';
                                    }}
                                    onDragEnd={(e) => {
                                      if (e.currentTarget) e.currentTarget.style.opacity = '1';
                                      setDraggedItem(null);
                                      setDropTarget(null);
                                    }}
                                    title={isCloneable ? dragTitle : ''}
                                    data-testid={`recent-workout-${item.workout.id}`}
                                  >
                                    {/* Hover drag glyph — top-right */}
                                    {isCloneable && (
                                      <span
                                        className={`absolute top-0 right-0.5 text-[10px] opacity-0 group-hover:opacity-90 transition-opacity pointer-events-none ${item.is_finished ? 'text-purple-300' : 'text-cyan-300'}`}
                                      >
                                        {dragGlyph}
                                      </span>
                                    )}
                                    {focus && (
                                      <span className="inline-flex items-center gap-1">
                                        <span
                                          className={`text-[9px] font-bold px-1 py-0 rounded-full ${focus.color} inline-block pointer-events-none`}
                                        >
                                          {focus.label}
                                        </span>
                                        {item.is_finished && (
                                          <Check className="w-2.5 h-2.5 text-green-500 pointer-events-none" />
                                        )}
                                        {item.workout.is_deload && (
                                          <span
                                            className="text-[9px] px-1 py-0 rounded-full bg-amber-500/25 text-amber-200 border border-amber-400/40 leading-none pointer-events-none"
                                            title="Deload week"
                                          >
                                            🪶
                                          </span>
                                        )}
                                      </span>
                                    )}
                                    <ol className="text-[9px] text-gray-400 mt-0.5 list-decimal list-inside pointer-events-none leading-tight">
                                      {item.workout.exercises.slice(0, 3).map((ex, i) => (
                                        <li key={i} className="truncate">{ex.exercise_name}</li>
                                      ))}
                                      {item.workout.exercises.length > 3 && (
                                        <li className="text-gray-600 list-none">+{item.workout.exercises.length - 3} más</li>
                                      )}
                                    </ol>
                                  </div>
                                ) : (
                                  <div className="text-[9px] text-gray-600 px-1 py-0.5 rounded bg-gray-800/15 border border-gray-700/15">
                                    {formatTime(item.booking.start_time)}
                                  </div>
                                )}
                              </div>
                            );
                          }) : (
                            <div className="text-[9px] text-gray-700 text-center py-0.5">—</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {!hasAnything && (
                    <div className="text-[10px] text-gray-700 text-center py-0.5 italic">Sin sesiones</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== CURRENT WEEK — Active Planner (Drag & Drop) ===== */}
      <div>
        <div className="flex items-center gap-3 mb-1.5">
          <span className="text-xs font-semibold text-blue-400 uppercase tracking-wider">This Week</span>
        </div>

      {/* Weekly Grid — Drag & Drop enabled */}
      <div className="grid grid-cols-6 gap-2">
        {dayKeys.map((dayKey, index) => {
          const dayData = plannerData?.days?.[dayKey];
          const isToday = dayData?.date === formatLocalDate(new Date());
          const isHighlighted = highlightedDate && dayData?.date === highlightedDate;
          const isDragOver = dropTarget === dayKey && draggedItem;
          const showConfirm = dropConfirm?.dayKey === dayKey;
          
          return (
            <div
              key={dayKey}
              className={`rounded-xl p-3 min-h-[200px] transition-all ${
                isDragOver
                  ? 'bg-blue-800/50 border-2 border-blue-400 border-dashed ring-2 ring-blue-400/30 scale-[1.02]'
                  : isHighlighted 
                    ? 'bg-amber-900/40 border-2 border-amber-400 ring-2 ring-amber-400/30' 
                    : isToday 
                      ? 'bg-blue-900/50 border-2 border-blue-400 ring-1 ring-blue-400/20' 
                      : 'bg-gray-800/50 border border-transparent'
              }`}
              data-testid={`day-${dayKey}`}
              onDragOver={(e) => handleDragOver(e, dayKey)}
              onDragLeave={(e) => handleDragLeave(e, dayKey)}
              onDrop={(e) => handleDrop(e, dayKey, dayData)}
            >
              {/* Day Header */}
              <div className="text-center mb-3">
                <div className={`text-sm font-medium ${
                  isDragOver ? 'text-blue-300' : isHighlighted ? 'text-amber-300' : isToday ? 'text-blue-400' : 'text-gray-400'
                }`}>
                  {dayLabels[index]}
                </div>
                <div className={`text-lg font-bold ${
                  isDragOver ? 'text-blue-200' : isHighlighted ? 'text-amber-200' : isToday ? 'text-white' : 'text-gray-300'
                }`}>
                  {dayData?.date ? new Date(dayData.date + 'T12:00:00').getDate() : ''}
                </div>
              </div>

              {/* Drop zone indicator when dragging */}
              {isDragOver && (
                <div className="mb-2 px-2 py-1.5 bg-blue-500/15 border border-blue-400/30 rounded-lg text-center">
                  <ArrowRight className="w-3.5 h-3.5 text-blue-400 inline mr-1" />
                  <span className="text-blue-300 text-xs font-medium">Drop here</span>
                </div>
              )}

              {/* Inline confirm for creating new booking on drop */}
              {showConfirm && (
                <div className="mb-2 p-2.5 bg-emerald-900/40 border border-emerald-500/40 rounded-lg animate-in fade-in" data-testid="drop-confirm">
                  <p className="text-emerald-300 text-xs font-semibold mb-2">Create session at:</p>
                  <input
                    ref={dropConfirmTimeRef}
                    type="time"
                    defaultValue={dropConfirm.time}
                    className="w-full px-2 py-1 bg-gray-900/60 border border-emerald-500/30 rounded text-white text-xs mb-2"
                    data-testid="drop-confirm-time"
                  />
                  <div className="flex gap-1.5">
                    <button
                      onClick={handleConfirmDrop}
                      disabled={isMoving}
                      className="flex-1 text-xs py-1 px-2 rounded bg-emerald-600/40 text-emerald-200 hover:bg-emerald-600/60 transition disabled:opacity-50 font-medium"
                      data-testid="drop-confirm-btn"
                    >
                      {isMoving ? 'Moving...' : 'Confirm'}
                    </button>
                    <button
                      onClick={handleCancelDrop}
                      className="flex-1 text-xs py-1 px-2 rounded bg-gray-600/40 text-gray-300 hover:bg-gray-600/60 transition"
                      data-testid="drop-cancel-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Bookings */}
              <div className="space-y-2">
                {dayData?.bookings?.length > 0 ? (
                  dayData.bookings.map((item, idx) => {
                    // Read-only card for finished Rutinas libres (no booking).
                    if (item.is_flexible) {
                      return (
                        <div
                          key={item.booking.id}
                          className="rounded-lg p-2 text-sm bg-amber-900/25 border border-amber-500/30"
                          data-testid={`flexible-planner-card-${item.workout.id}`}
                        >
                          <div className="flex items-center gap-1 text-[11px] text-amber-300 mb-1">
                            <Home className="w-3 h-3 flex-shrink-0" />
                            <span className="font-semibold">Rutina libre</span>
                            {item.is_finished && <Check className="w-3 h-3 text-green-400 ml-auto flex-shrink-0" />}
                          </div>
                          {item.workout.template_name && (
                            <p className="text-[11px] text-amber-100/90 truncate mb-1">{item.workout.template_name}</p>
                          )}
                          {item.workout.exercises && item.workout.exercises.length > 0 ? (
                            <ol className="text-[10px] text-amber-200/80 space-y-0.5 list-decimal list-inside">
                              {item.workout.exercises.map((ex, exIdx) => (
                                <li key={exIdx} className="truncate leading-tight">
                                  <span className="text-amber-100">{ex.exercise_name}</span>
                                  {ex.is_superset && (
                                    <span className="text-orange-300 ml-1">(SS-{ex.superset_group || 'A'})</span>
                                  )}
                                </li>
                              ))}
                            </ol>
                          ) : (
                            <span className="text-[10px] text-amber-200/60">Sin ejercicios</span>
                          )}
                        </div>
                      );
                    }
                    const isDraggable = item.has_workout && item.workout && item.workout.status !== 'in_progress';
                    
                    return (
                      <div
                        key={item.booking.id}
                        draggable={isDraggable}
                        onDragStart={(e) => isDraggable && handleDragStart(e, item)}
                        onDragEnd={handleDragEnd}
                        className={`rounded-lg p-2 text-sm transition-all ${
                          item.has_workout 
                            ? `bg-purple-900/40 border border-purple-500/30 ${isDraggable ? 'cursor-grab active:cursor-grabbing hover:border-purple-400/60' : ''}` 
                            : 'bg-gray-700/50 border border-gray-600/30'
                        } ${draggedItem?.workoutId === item.workout?.id ? 'opacity-40' : ''}`}
                        data-testid={`booking-card-${item.booking.id}`}
                      >
                        {/* Time + drag handle */}
                        <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
                          {isDraggable && <GripVertical className="w-3 h-3 text-purple-400/60 flex-shrink-0" />}
                          <Clock className="w-3 h-3" />
                          {formatTime(item.booking.start_time)}
                        </div>
                        
                        {/* Status Badge */}
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(item.booking.status)}`}>
                          {item.booking.status}
                        </span>
                        
                        {/* Workout Info - Only show if workout exists */}
                        {item.has_workout && item.workout && (
                          <div className="mt-2">
                            {/* Full Exercise List - Compact */}
                            {item.workout.exercises && item.workout.exercises.length > 0 ? (
                              <ol className="text-[10px] text-purple-200/90 space-y-0.5 mb-2 list-decimal list-inside">
                                {item.workout.exercises.map((ex, exIdx) => (
                                  <li key={exIdx} className="truncate leading-tight">
                                    <span className="text-purple-100">{ex.exercise_name}</span>
                                    {ex.is_superset && (
                                      <span className="text-orange-300 ml-1">(SS-{ex.superset_group || 'A'})</span>
                                    )}
                                  </li>
                                ))}
                              </ol>
                            ) : (
                              <div className="flex items-center gap-1 text-purple-300 text-xs mb-2">
                                <Dumbbell className="w-3 h-3" />
                                No exercises
                              </div>
                            )}
                            {item.is_finished && (
                              <div className="flex items-center gap-1 text-green-400 text-xs">
                                <Check className="w-3 h-3" />
                                Completed
                              </div>
                            )}
                            <button
                              onClick={() => handleEditWorkout(item.booking, item.workout)}
                              className="mt-2 w-full text-xs py-1 px-2 rounded bg-purple-600/30 text-purple-300 hover:bg-purple-600/50 transition-colors flex items-center justify-center gap-1"
                              data-testid={`edit-workout-${item.workout.id}`}
                            >
                              <Edit3 className="w-3 h-3" />
                              Edit Workout
                            </button>
                            {/* Operational quick-action — trainer can tag/untag deload
                                without entering the FullScreenWorkoutEditor. */}
                            <div className="mt-1 flex justify-center">
                              <DeloadToggleButton
                                workoutId={item.workout.id}
                                isDeload={!!item.workout.is_deload}
                                variant="pill"
                                testIdSuffix={`planner-${item.workout.id}`}
                                onChange={(next) => {
                                  // mutate the in-place item so the next render
                                  // reflects state without waiting for a full refetch.
                                  item.workout.is_deload = next;
                                  if (typeof refreshContinuity === 'function') refreshContinuity();
                                }}
                              />
                            </div>
                          </div>
                        )}
                        
                        {/* Assign Template or Create Workout - Only show if NO workout */}
                        {!item.has_workout && (
                          <div className="mt-2 space-y-1">
                            <button
                              onClick={() => handleCreateWorkout(item.booking)}
                              className="w-full text-xs py-1 px-2 rounded bg-green-600/30 text-green-300 hover:bg-green-600/50 transition-colors flex items-center justify-center gap-1"
                              data-testid={`create-workout-${item.booking.id}`}
                            >
                              <Plus className="w-3 h-3" />
                              Create Workout
                            </button>
                            <button
                              onClick={() => {
                                setSelectedBooking(item);
                                setShowTemplateModal(true);
                              }}
                              className="w-full text-xs py-1 px-2 rounded bg-blue-600/30 text-blue-300 hover:bg-blue-600/50 transition-colors flex items-center justify-center gap-1"
                              data-testid={`assign-template-${item.booking.id}`}
                            >
                              <Dumbbell className="w-3 h-3" />
                              Use Template
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className={`text-center text-xs py-4 ${isDragOver ? 'text-blue-400' : 'text-gray-500'}`}>
                    {isDragOver ? 'Drop to create session' : 'No bookings'}
                  </div>
                )}
              </div>
              {dayData?.date && (
                <button
                  onClick={() => setReserveDate(dayData.date)}
                  className="mt-2 w-full text-[11px] py-1 px-2 rounded bg-blue-600/20 text-blue-300 hover:bg-blue-600/40 transition flex items-center justify-center gap-1"
                  data-testid={`reserve-slot-${dayKey}`}
                >
                  <Plus className="w-3 h-3" /> Reserve slot
                </button>
              )}
            </div>
          );
        })}
      </div>
      </div>

      {/* Template Selection Modal - Enhanced */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="template-modal">
          <div className="bg-gray-800 rounded-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-700 flex-shrink-0">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-bold text-white">Select Template</h3>
                  <p className="text-sm text-gray-400 mt-1">
                    Assigning to: {formatTime(selectedBooking?.booking?.start_time)} on {selectedBooking?.booking?.date}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowTemplateModal(false);
                    setSelectedBooking(null);
                  }}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition"
                >
                  ✕
                </button>
              </div>
            </div>
            
            {/* Template Selector Panel */}
            <div className="flex-1 overflow-y-auto p-4">
              <TemplateSelectorPanel
                templates={templates}
                onSelectTemplate={(template) => handleAssignTemplate(template.id)}
                onClose={() => {
                  setShowTemplateModal(false);
                  setSelectedBooking(null);
                }}
                isLoading={assigningTemplate}
              />
            </div>
          </div>
        </div>
      )}

      {/* Reserve a gym slot straight from the planner */}
      {reserveDate && (
        <DayReserveModal
          date={reserveDate}
          clientId={clientId}
          clientName={clientName}
          onClose={() => setReserveDate(null)}
          onBooked={() => { setReserveDate(null); fetchPlannerData(); }}
        />
      )}

      {/* Direct Workout Editor - No URL params hack */}
      {editingWorkout && (
        <FullScreenWorkoutEditor
          booking={editingWorkout.booking}
          workout={editingWorkout.workout}
          mode={editingWorkout.mode}
          exercises={exercises}
          templates={templates}
          onSave={handleSaveWorkout}
          onClose={() => setEditingWorkout(null)}
          onFinishWorkout={handleFinishWorkout}
          clientId={clientId}
          t={t || ((key) => key)}
        />
      )}
    </div>
  );
};

export default BookingPlanner;
